import { FC, useState } from "react"
import { useForm } from "react-hook-form"
import { InventoryItem } from "../../../lib/types"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  Card,
  CardContent
} from "../../../components/ui"
import {
  Printer,
  FileText,
  Ruler,
  Grid3x3,
  AlertCircle,
  CheckCircle2
} from "lucide-react"
import { toast } from "sonner"
import jsPDF from "jspdf"
import JsBarcode from "jsbarcode"
import { BarcodePrintPreview } from "./barcode-print-preview"

type BarcodePrintDialogProps = {
  isShowing: boolean
  close: () => void
  selectedItems: InventoryItem[]
}

type PrintSettings = {
  labelWidth: number
  labelHeight: number
  labelsPerPage: number
  copiesPerItem: number
  pageMarginTop: number
  pageMarginLeft: number
  labelSpacingX: number
  labelSpacingY: number
  fontSize: number
  showItemName: boolean
  showSKU: boolean
  barcodeHeight: number
  pageSize: 'letter' | 'a4' | 'legal'
  orientation: 'portrait' | 'landscape'
}

// Common label presets
const LABEL_PRESETS = [
  { name: 'Avery 5160 (1" x 2.625")', width: 2.625, height: 1, perPage: 30 },
  { name: 'Avery 5163 (2" x 4")', width: 4, height: 2, perPage: 10 },
  { name: 'Avery 8160 (1" x 2.625")', width: 2.625, height: 1, perPage: 30 },
  { name: 'Avery 8163 (2" x 4")', width: 4, height: 2, perPage: 10 },
  { name: 'Avery 5167 (0.5" x 1.75")', width: 1.75, height: 0.5, perPage: 80 },
  { name: 'Custom', width: 0, height: 0, perPage: 0 }
]

export const BarcodePrintDialog: FC<BarcodePrintDialogProps> = ({
  isShowing,
  close,
  selectedItems
}) => {
  const { register, getValues, setValue, watch } = useForm<PrintSettings>({
    defaultValues: {
      labelWidth: 2.625,
      labelHeight: 1,
      labelsPerPage: 30,
      copiesPerItem: 1,
      pageMarginTop: 0.5,
      pageMarginLeft: 0.1875,
      labelSpacingX: 0.125,
      labelSpacingY: 0,
      fontSize: 8,
      showItemName: true,
      showSKU: true,
      barcodeHeight: 40,
      pageSize: 'letter',
      orientation: 'portrait'
    }
  })

  const [selectedPreset, setSelectedPreset] = useState('Avery 5160 (1" x 2.625")')
  const [isGenerating, setIsGenerating] = useState(false)

  const watchedSettings = watch()

  const handlePresetChange = (presetName: string) => {
    setSelectedPreset(presetName)
    const preset = LABEL_PRESETS.find(p => p.name === presetName)
    if (preset && preset.name !== 'Custom') {
      setValue('labelWidth', preset.width)
      setValue('labelHeight', preset.height)
      setValue('labelsPerPage', preset.perPage)
    }
  }

  const generateBarcodePDF = async () => {
    setIsGenerating(true)

    try {
      const settings = getValues()

      // Page dimensions in inches
      const pageDimensions = {
        letter: { width: 8.5, height: 11 },
        a4: { width: 8.27, height: 11.69 },
        legal: { width: 8.5, height: 14 }
      }

      const pageDim = pageDimensions[settings.pageSize]
      const pageWidth = settings.orientation === 'portrait' ? pageDim.width : pageDim.height
      const pageHeight = settings.orientation === 'portrait' ? pageDim.height : pageDim.width

      // Create PDF
      const pdf = new jsPDF({
        orientation: settings.orientation,
        unit: 'in',
        format: settings.pageSize
      })

      // Calculate labels per row and column based on page constraints
      let labelsPerRow: number
      let labelsPerColumn: number
      let actualLabelsPerPage: number

      // Always honor the user's labelsPerPage setting
      actualLabelsPerPage = settings.labelsPerPage

      if (actualLabelsPerPage === 1) {
        // Special case: single label centered on page
        labelsPerRow = 1
        labelsPerColumn = 1
      } else {
        // Calculate maximum labels that can fit per row based on page width
        const availableWidth = pageWidth - (settings.pageMarginLeft * 2)
        const maxLabelsPerRow = Math.floor(availableWidth / settings.labelWidth)

        // Calculate maximum labels that can fit per column based on page height
        const availableHeight = pageHeight - (settings.pageMarginTop * 2)
        const maxLabelsPerColumn = Math.floor(availableHeight / settings.labelHeight)

        // Ensure we don't exceed the physical constraints of the page
        if (maxLabelsPerRow < 1 || maxLabelsPerColumn < 1) {
          throw new Error('Labels are too large to fit on the selected page size. Please reduce label dimensions or increase page size.')
        }

        // Try to make a reasonably square grid, but respect page width constraints
        const idealLabelsPerRow = Math.ceil(Math.sqrt(actualLabelsPerPage))
        labelsPerRow = Math.min(idealLabelsPerRow, maxLabelsPerRow)
        labelsPerColumn = Math.ceil(actualLabelsPerPage / labelsPerRow)

        // Verify the layout doesn't exceed page height
        if (labelsPerColumn > maxLabelsPerColumn) {
          // Too many rows, need to adjust
          labelsPerColumn = maxLabelsPerColumn
          // Recalculate labels per page based on constraints
          actualLabelsPerPage = labelsPerRow * labelsPerColumn
          console.warn(`Adjusted labels per page from ${settings.labelsPerPage} to ${actualLabelsPerPage} to fit page dimensions`)
        }
      }

      // Generate all labels (items * copies)
      const allLabels: InventoryItem[] = []
      selectedItems.forEach(item => {
        for (let i = 0; i < settings.copiesPerItem; i++) {
          allLabels.push(item)
        }
      })

      let labelIndex = 0
      let pageCount = 0

      for (const item of allLabels) {
        // Add new page if needed (before calculating position)
        if (labelIndex > 0) {
          const labelOnPage = labelIndex % actualLabelsPerPage
          if (labelOnPage === 0) {
            pdf.addPage()
            pageCount++
          }
        }

        // Calculate position on page
        const labelOnPage = labelIndex % actualLabelsPerPage
        const row = Math.floor(labelOnPage / labelsPerRow)
        const col = labelOnPage % labelsPerRow

        // Calculate label position
        let x: number
        let y: number

        if (actualLabelsPerPage === 1) {
          // Center the label on the page when only 1 label per page
          x = (pageWidth - settings.labelWidth) / 2
          y = (pageHeight - settings.labelHeight) / 2
        } else {
          // Calculate available space for the grid
          const availableWidth = pageWidth - (settings.pageMarginLeft * 2)
          const availableHeight = pageHeight - (settings.pageMarginTop * 2)

          // Calculate total space needed for all labels
          const totalLabelsWidth = labelsPerRow * settings.labelWidth
          const totalLabelsHeight = labelsPerColumn * settings.labelHeight

          // Calculate spacing to distribute labels evenly
          const horizontalSpacing = labelsPerRow > 1
            ? (availableWidth - totalLabelsWidth) / (labelsPerRow - 1)
            : 0
          const verticalSpacing = labelsPerColumn > 1
            ? (availableHeight - totalLabelsHeight) / (labelsPerColumn - 1)
            : 0

          // Position labels with even spacing
          x = settings.pageMarginLeft + (col * (settings.labelWidth + horizontalSpacing))
          y = settings.pageMarginTop + (row * (settings.labelHeight + verticalSpacing))
        }

        // Generate barcode on canvas
        const canvas = document.createElement('canvas')

        try {
          const skuValue = String(item.sku || 'NO-SKU')
          JsBarcode(canvas, skuValue, {
            format: 'CODE128',
            width: 2,
            height: settings.barcodeHeight,
            displayValue: settings.showSKU,
            fontSize: settings.fontSize,
            margin: 5
          })

          const barcodeImage = canvas.toDataURL('image/png')

          // Calculate barcode dimensions
          const barcodeAspectRatio = canvas.width / canvas.height
          const barcodeHeightInches = settings.labelHeight * 0.6 // 60% of label height for barcode
          const barcodeWidthInches = barcodeHeightInches * barcodeAspectRatio

          // Center barcode horizontally in label
          const barcodeX = x + (settings.labelWidth - barcodeWidthInches) / 2
          let currentY = y + 0.05 // Start with small padding

          // Add item name if enabled
          if (settings.showItemName && item.name) {
            pdf.setFontSize(settings.fontSize)
            pdf.setFont('helvetica', 'bold')

            // Wrap text if too long
            const maxWidth = settings.labelWidth - 0.1
            const text = String(item.name || '')
            const lines = pdf.splitTextToSize(text, maxWidth)
            const textLines = Array.isArray(lines) ? lines.slice(0, 2) : [lines] // Max 2 lines

            textLines.forEach((line: string, idx: number) => {
              if (line && typeof line === 'string') {
                const textWidth = pdf.getTextWidth(line)
                const textX = x + (settings.labelWidth - textWidth) / 2
                pdf.text(line, textX, currentY + (idx * 0.1) + 0.08)
              }
            })

            currentY += (textLines.length * 0.1) + 0.05
          }

          // Add barcode image
          const barcodeY = currentY
          pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidthInches, barcodeHeightInches)

          // Draw border for preview (optional - remove in production)
          // pdf.setDrawColor(200, 200, 200)
          // pdf.rect(x, y, settings.labelWidth, settings.labelHeight)

        } catch (error) {
          console.error('Error generating barcode for item:', item.sku, error)
          // Draw error placeholder
          try {
            pdf.setFontSize(8)
            pdf.setFont('helvetica', 'normal')
            pdf.text('Error generating barcode', x + 0.05, y + (settings.labelHeight / 2))
          } catch (textError) {
            console.error('Error drawing error message:', textError)
          }
        }

        labelIndex++
      }

      // Save PDF
      const fileName = `barcodes_${new Date().getTime()}.pdf`
      pdf.save(fileName)

      toast.success(`Generated ${allLabels.length} label(s) across ${pageCount + 1} page(s)`)
      close()

    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate barcode PDF')
    } finally {
      setIsGenerating(false)
    }
  }

  const totalLabels = selectedItems.length * watchedSettings.copiesPerItem

  // Calculate actual labels per page that will fit (matching PDF generation logic)
  const calculateActualLabelsPerPage = (): number => {
    const settings = watchedSettings

    // Page dimensions in inches
    const pageDimensions = {
      letter: { width: 8.5, height: 11 },
      a4: { width: 8.27, height: 11.69 },
      legal: { width: 8.5, height: 14 }
    }

    const pageDim = pageDimensions[settings.pageSize]
    const pageWidth = settings.orientation === 'portrait' ? pageDim.width : pageDim.height
    const pageHeight = settings.orientation === 'portrait' ? pageDim.height : pageDim.width

    let actualLabelsPerPage = settings.labelsPerPage

    if (actualLabelsPerPage === 1) {
      return 1
    }

    // Calculate maximum labels that can fit per row based on page width
    const availableWidth = pageWidth - (settings.pageMarginLeft * 2)
    const maxLabelsPerRow = Math.floor(availableWidth / settings.labelWidth)

    // Calculate maximum labels that can fit per column based on page height
    const availableHeight = pageHeight - (settings.pageMarginTop * 2)
    const maxLabelsPerColumn = Math.floor(availableHeight / settings.labelHeight)

    if (maxLabelsPerRow < 1 || maxLabelsPerColumn < 1) {
      return 1 // Fallback
    }

    // Try to make a reasonably square grid, but respect page width constraints
    const idealLabelsPerRow = Math.ceil(Math.sqrt(actualLabelsPerPage))
    const labelsPerRow = Math.min(idealLabelsPerRow, maxLabelsPerRow)
    let labelsPerColumn = Math.ceil(actualLabelsPerPage / labelsPerRow)

    // Verify the layout doesn't exceed page height
    if (labelsPerColumn > maxLabelsPerColumn) {
      labelsPerColumn = maxLabelsPerColumn
      actualLabelsPerPage = labelsPerRow * labelsPerColumn
    }

    return actualLabelsPerPage
  }

  const actualLabelsPerPage = calculateActualLabelsPerPage()

  return (
    <Dialog open={isShowing} onOpenChange={close}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-neutral800 dark:border-neutral700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl dark:text-neutral100">
            <Printer className="text-sumoPrimary" width={28} height={28} />
            Print Barcode Labels
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary and Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Summary Card */}
            <Card className="bg-sumoCream/30 border-sumoPrimary/30 dark:bg-neutral700/50 dark:border-sumoPrimary/50">
              <CardContent className="pt-4">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-neutral600 dark:text-neutral300">Selected Items</p>
                      <p className="text-2xl font-bold text-neutral900 dark:text-neutral100">{selectedItems.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral600 dark:text-neutral300">Total Labels</p>
                      <p className="text-2xl font-bold text-sumoPrimary">{totalLabels}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral600 dark:text-neutral300">Estimated Pages</p>
                      <p className="text-2xl font-bold text-sumoTeal">
                        {Math.ceil(totalLabels / actualLabelsPerPage)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <BarcodePrintPreview settings={watchedSettings} />
          </div>

          {/* Label Preset Selection */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral700 dark:text-neutral300">
              <FileText width={16} height={16} className="text-sumoPrimary" />
              Label Template
            </label>
            <Select value={selectedPreset} onValueChange={handlePresetChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a label template" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Common Label Sizes</SelectLabel>
                  {LABEL_PRESETS.map(preset => (
                    <SelectItem key={preset.name} value={preset.name}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column: Label Dimensions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-neutral900 dark:text-neutral100 flex items-center gap-2">
                <Ruler className="text-sumoPrimary" width={18} height={18} />
                Label Dimensions (inches)
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Label Width</label>
                  <Input
                    type="number"
                    step="0.125"
                    {...register('labelWidth', { valueAsNumber: true })}
                    disabled={selectedPreset !== 'Custom'}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Label Height</label>
                  <Input
                    type="number"
                    step="0.125"
                    {...register('labelHeight', { valueAsNumber: true })}
                    disabled={selectedPreset !== 'Custom'}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Labels Per Page</label>
                  <Input
                    type="number"
                    {...register('labelsPerPage', { valueAsNumber: true })}
                    disabled={selectedPreset !== 'Custom'}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Copies Per Item</label>
                  <Input
                    type="number"
                    min="1"
                    {...register('copiesPerItem', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Page Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold text-neutral900 dark:text-neutral100 flex items-center gap-2">
                <Grid3x3 className="text-sumoPrimary" width={18} height={18} />
                Page & Spacing Settings
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Page Size</label>
                  <Select
                    value={watchedSettings.pageSize}
                    onValueChange={(value: any) => setValue('pageSize', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="letter">Letter (8.5" × 11")</SelectItem>
                      <SelectItem value="a4">A4 (8.27" × 11.69")</SelectItem>
                      <SelectItem value="legal">Legal (8.5" × 14")</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Orientation</label>
                  <Select
                    value={watchedSettings.orientation}
                    onValueChange={(value: any) => setValue('orientation', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Top Margin</label>
                    <Input
                      type="number"
                      step="0.0625"
                      {...register('pageMarginTop', { valueAsNumber: true })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Left Margin</label>
                    <Input
                      type="number"
                      step="0.0625"
                      {...register('pageMarginLeft', { valueAsNumber: true })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Horiz. Spacing</label>
                    <Input
                      type="number"
                      step="0.0625"
                      {...register('labelSpacingX', { valueAsNumber: true })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Vert. Spacing</label>
                    <Input
                      type="number"
                      step="0.0625"
                      {...register('labelSpacingY', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Display Options */}
          <div className="space-y-3">
            <h3 className="font-semibold text-neutral900 dark:text-neutral100">Display Options</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showItemName"
                  {...register('showItemName')}
                  className="w-4 h-4 accent-sumoPrimary"
                />
                <label htmlFor="showItemName" className="text-sm text-neutral700 dark:text-neutral300 cursor-pointer">
                  Show Item Name
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showSKU"
                  {...register('showSKU')}
                  className="w-4 h-4 accent-sumoPrimary"
                />
                <label htmlFor="showSKU" className="text-sm text-neutral700 dark:text-neutral300 cursor-pointer">
                  Show SKU Below Barcode
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Font Size</label>
                <Input
                  type="number"
                  min="6"
                  max="14"
                  {...register('fontSize', { valueAsNumber: true })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral600 dark:text-neutral300">Barcode Height (px)</label>
                <Input
                  type="number"
                  min="20"
                  max="80"
                  {...register('barcodeHeight', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          {/* Warning if no items selected */}
          {selectedItems.length === 0 && (
            <Card className="bg-warning50 border-warning500 dark:bg-warning900/20 dark:border-warning700">
              <CardContent className="pt-4 flex items-start gap-3">
                <AlertCircle className="text-warning700 dark:text-warning500 flex-shrink-0" width={20} height={20} />
                <div className="text-sm">
                  <p className="font-semibold text-warning900 dark:text-warning300">No items selected</p>
                  <p className="text-warning700 dark:text-warning400">Please select items from the inventory table to print labels.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={generateBarcodePDF}
            disabled={selectedItems.length === 0 || isGenerating}
            className="flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <span className="animate-spin">⏳</span>
                Generating...
              </>
            ) : (
              <>
                <CheckCircle2 width={16} height={16} />
                Generate PDF ({totalLabels} labels)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
